/**
 * OfflineBanner — top-of-page banner shown when the dashboard can't reach the
 * BuzzerAI backend. Detects both `navigator.onLine === false` AND API fetch
 * failures (so it also catches "internet up but our server unreachable").
 *
 * The cached queue underneath keeps showing so the host stand is never blind.
 */
import { useEffect, useState } from "react";
import { WifiSlash, ArrowClockwise } from "@phosphor-icons/react";

export default function OfflineBanner({ offline, lastSyncedAt, onRetry }) {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!offline) return;
    const t = setInterval(() => setNow(Date.now()), 5000);
    return () => clearInterval(t);
  }, [offline]);

  if (!offline) return null;

  const ago = lastSyncedAt ? Math.max(0, Math.floor((now - lastSyncedAt) / 60000)) : null;
  const agoLabel = ago === null ? "never" : ago === 0 ? "moments ago" : `${ago}m ago`;

  return (
    <div
      className="mb-4 p-3 rounded-lg bg-[#D96C5B]/10 border border-[#D96C5B]/40 flex items-center justify-between gap-3 flex-wrap"
      data-testid="offline-banner"
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <div className="w-9 h-9 rounded-lg bg-[#D96C5B] flex items-center justify-center shrink-0">
          <WifiSlash size={18} color="#fff" weight="fill" />
        </div>
        <div className="min-w-0">
          <div className="text-sm font-semibold text-[#121914]">You're offline — showing last known queue</div>
          <div className="text-xs text-[#5C6B61] mt-0.5">
            Last synced {agoLabel}. New arrivals from the AI receptionist will appear when your connection is back.
            Anything you tap (Notify, Seat, Remove) will fail until then.
          </div>
        </div>
      </div>
      <button
        type="button"
        onClick={onRetry}
        className="text-xs font-semibold px-3 py-2 rounded-full bg-[#1E3A2F] text-white hover:bg-[#2b5648] inline-flex items-center gap-1.5"
        data-testid="offline-retry"
      >
        <ArrowClockwise size={12} weight="bold" /> Try reconnecting
      </button>
    </div>
  );
}
